// src/index.ts
import { extendTheme as extendTheme2 } from "@chakra-ui/react";

// src/utils/fontUtils.ts
import { extendTheme } from "@chakra-ui/react";
import { fontFamilyMain, fontFamilyZhCn, fontFamilyAr } from "@airhelp/design-tokens";
var localizeFontFamily = (languageCode) => {
  switch (languageCode.toLowerCase()) {
    case "zh-cn":
      return fontFamilyZhCn;
    case "ar":
      return fontFamilyAr;
    default:
      return fontFamilyMain;
  }
};
function localizeTheme(languageCode, chakraTheme = theme) {
  const fontFamily = localizeFontFamily(languageCode);
  return extendTheme({
    ...chakraTheme,
    fonts: {
      ...chakraTheme.fonts,
      body: fontFamily,
      heading: fontFamily
    }
  });
}

// src/components/Alert.tsx
import {
  spacing2x,
  spacing3x,
  spacing4x,
  spacing6x,
  borderRadiusSm,
  fontSizeSm,
  fontWeightMedium
} from "@airhelp/design-tokens";
import { alertAnatomy } from "@chakra-ui/anatomy";
import { createMultiStyleConfigHelpers } from "@chakra-ui/react";
var { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(alertAnatomy.keys);
var baseStyle = definePartsStyle((props) => {
  const { status } = props;
  const defaultBase = {
    container: {
      button: {
        background: "rgba(255,255,255,0)",
        top: "unset",
        right: spacing4x,
        width: spacing6x,
        height: spacing6x,
        borderRadius: borderRadiusSm,
        padding: 0,
        svg: {
          boxSize: spacing3x
        },
        _hover: {
          background: "rgba(255,255,255,0.16)"
        },
        _active: {
          background: "rgba(255,255,255,0.24)"
        }
      }
    },
    title: {
      fontSize: fontSizeSm,
      fontWeight: fontWeightMedium,
      paddingRight: spacing2x
    },
    description: {
      fontSize: fontSizeSm
    }
  };
  const successBase = status === "success" && {
    container: {
      ...defaultBase.container,
      background: "success.600",
      color: "greyscale.100"
    },
    icon: {
      color: "greyscale.100"
    },
    title: {
      ...defaultBase.title,
      color: "greyscale.100"
    },
    description: {
      ...defaultBase.description,
      color: "greyscale.100"
    }
  };
  const warningBase = status === "warning" && {
    container: {
      ...defaultBase.container,
      background: "warning.500",
      color: "greyscale.100"
    },
    icon: {
      color: "greyscale.100"
    },
    title: {
      ...defaultBase.title,
      color: "greyscale.100"
    },
    description: {
      ...defaultBase.description,
      color: "greyscale.100",
      fontSize: "sm"
    }
  };
  const errorBase = status === "error" && {
    container: {
      ...defaultBase.container,
      background: "secondary.500",
      color: "greyscale.100"
    },
    icon: {
      color: "greyscale.100"
    },
    title: {
      ...defaultBase.title,
      color: "greyscale.100"
    },
    description: {
      ...defaultBase.description,
      color: "greyscale.100",
      fontSize: "sm"
    }
  };
  const infoBase = status === "info" && {
    container: {
      ...defaultBase.container,
      background: "primary.600",
      color: "greyscale.100"
    },
    icon: {
      color: "greyscale.100"
    },
    title: {
      ...defaultBase.title,
      color: "greyscale.100"
    },
    description: {
      ...defaultBase.description,
      color: "greyscale.100",
      fontSize: "sm"
    }
  };
  const loadingBase = status === "loading" && {
    container: {
      ...defaultBase.container,
      background: "primary.600",
      color: "greyscale.100"
    },
    icon: {
      color: "greyscale.100"
    },
    title: {
      ...defaultBase.title,
      color: "greyscale.100"
    },
    description: {
      ...defaultBase.description,
      color: "greyscale.100",
      fontSize: "sm"
    }
  };
  return {
    ...successBase,
    ...warningBase,
    ...errorBase,
    ...infoBase,
    ...loadingBase
  };
});
var Alert = defineMultiStyleConfig({ baseStyle });
var Alert_default = Alert;

// src/components/Accordion.ts
var baseStyleButton = {
  _expanded: { bg: "transparent", boxShadow: "none" },
  _hover: { bg: "transparent" },
  py: { base: 4, lg: 6 }
};
var baseStylePanel = {
  pb: { base: 4, lg: 6 }
};
var baseStyleIcon = {
  color: "primary.500",
  boxSize: 6,
  ml: 2
};
var primary = {
  container: {
    bg: "transparent",
    borderColor: "greyscale.400",
    _first: { borderTop: "none" }
  },
  button: { px: 0 },
  panel: { px: 0, pt: { base: 0, lg: 1 } }
};
var secondaryButtonPanelStyles = {
  px: { base: 4, lg: 6 },
  bg: "greyscale.100",
  borderRadius: "lg",
  mb: 2
};
var secondary = {
  container: { border: "none" },
  button: {
    ...secondaryButtonPanelStyles,
    _expanded: { mb: 0, borderBottomRadius: "none", bg: "greyscale.100" },
    _hover: { bg: { md: "greyscale.100" } }
  },
  panel: {
    ...secondaryButtonPanelStyles,
    borderTopRadius: "none",
    pt: 0
  }
};
var variants = {
  primary,
  secondary
};
var baseStyle2 = {
  panel: baseStylePanel,
  button: baseStyleButton,
  icon: baseStyleIcon
};
var defaultProps = {
  variant: "primary"
};
var Accordion = {
  defaultProps,
  baseStyle: baseStyle2,
  variants
};
var Accordion_default = Accordion;

// src/components/Autocomplete.ts
import { borderRadiusMd as borderRadiusMd2, spacing12x, spacing3x as spacing3x3, spacing4x as spacing4x3, spacing5x as spacing5x2, spacing6x as spacing6x3 } from "@airhelp/design-tokens";

// src/components/Select.ts
import {
  borderRadiusMd,
  borderRadiusSm as borderRadiusSm2,
  spacing2x as spacing2x2,
  spacing3x as spacing3x2,
  spacing4x as spacing4x2,
  spacing5x,
  spacing6x as spacing6x2
} from "@airhelp/design-tokens";
function getSelectStyles() {
  return {
    container: (provided) => ({
      ...provided,
      width: "100%"
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "greyscale.500",
      marginRight: 0
    }),
    control: (provided, state) => ({
      ...provided,
      cursor: "pointer",
      borderBottomRadius: state.menuIsOpen ? 0 : borderRadiusMd,
      paddingY: `${spacing3x2} !important`,
      paddingX: `${spacing4x2} !important`,
      width: "auto",
      minWidth: "auto",
      minHeight: "auto",
      lineHeight: spacing5x,
      color: "font.primary",
      "> div": {
        padding: "unset"
      },
      _invalid: {
        borderColor: state.selectProps.colorScheme === "warning" ? "warning.600" : "danger.700",
        _hover: {
          borderColor: { md: state.selectProps.colorScheme === "warning" ? "warning.700" : "danger.800" }
        },
        _focus: {
          borderColor: "primary.500"
        }
      },
      input: {
        maxHeight: spacing6x2
      }
    }),
    menu: (provided) => ({
      ...provided,
      margin: "unset",
      zIndex: "10"
    }),
    menuList: (provided) => ({
      ...provided,
      color: "greyscale.700",
      background: "greyscale.100",
      border: "1px solid",
      borderColor: "primary.500",
      borderTop: "none !important",
      borderTopRadius: 0,
      paddingX: "unset",
      paddingY: "unset",
      maxHeight: "12.25em",
      minWidth: "auto",
      "::-webkit-scrollbar": {
        width: spacing2x2
      },
      "::-webkit-scrollbar-track": {
        background: "greyscale.100",
        borderRadius: borderRadiusMd,
        marginBottom: "3px"
      },
      "::-webkit-scrollbar-thumb": {
        background: "greyscale.400",
        borderRadius: "24px"
      },
      "::-webkit-scrollbar-thumb:hover": {
        background: { md: "greyscale.400" }
      }
    }),
    option: (provided, state) => ({
      ...provided,
      paddingX: `${spacing4x2} !important`,
      paddingY: `${spacing2x2} !important`,
      background: state.isFocused || state.isSelected ? "primary.100" : "greyscale.100",
      color: state.isFocused || state.isSelected ? "primary.500" : "font.primary",
      borderRadius: borderRadiusSm2,
      width: "auto",
      cursor: "pointer",
      _hover: {
        color: { md: "primary.500" }
      },
      _active: {
        background: "primary.200"
      }
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      background: state.isDisabled ? "greyscale.300" : "transparent",
      border: "none",
      color: state.selectProps.isDisabled ? "greyscale.500" : "greyscale.700",
      paddingX: "unset",
      transform: state.selectProps.menuIsOpen && "rotate(180deg)",
      display: "flex",
      justifyContent: state.selectProps.menuIsOpen ? "start" : "end",
      minWidth: "auto",
      svg: {
        boxSize: 5,
        color: "primary.900"
      }
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: "none"
    }),
    inputContainer: (provided) => ({
      ...provided,
      cursor: "text"
    })
  };
}

// src/components/Autocomplete.ts
var getAutocompleteStyles = () => {
  return {
    ...getSelectStyles(),
    placeholder: (provided) => ({
      ...provided,
      color: "greyscale.500",
      marginRight: 0
    }),
    control: (provided, state) => ({
      ...provided,
      cursor: "text",
      borderBottomRadius: state.menuIsOpen ? 0 : borderRadiusMd2,
      flexDirection: "row-reverse",
      paddingY: `${spacing3x3} !important`,
      paddingX: `${spacing4x3} !important`,
      width: "auto",
      minWidth: "auto",
      minHeight: "auto",
      lineHeight: spacing5x2,
      color: "font.primary",
      "> div": {
        padding: "unset",
        flexWrap: "nowrap",
        "> div": {
          marginLeft: "0",
          marginRight: "0"
        },
        "[class$=singleValue]": {
          color: "inherit"
        }
      },
      _invalid: {
        borderColor: state.selectProps.colorScheme === "warning" ? "warning.600" : "danger.700",
        _hover: {
          borderColor: { md: state.selectProps.colorScheme === "warning" ? "warning.700" : "danger.800" }
        },
        _focus: {
          borderColor: "primary.500"
        }
      },
      input: {
        maxHeight: spacing6x3
      }
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      div: {
        padding: "unset",
        marginRight: 0,
        pointerEvents: "none"
      }
    }),
    inputContainer: (provided) => ({
      ...provided,
      cursor: "text"
    })
  };
};
var getAutocompleteWithIconStyles = () => {
  return {
    ...getAutocompleteStyles(),
    placeholder: (provided) => ({
      ...provided,
      color: "greyscale.500",
      marginRight: 0
    }),
    control: (provided, state) => ({
      ...provided,
      cursor: "text",
      borderBottomRadius: state.menuIsOpen ? 0 : borderRadiusMd2,
      flexDirection: "row-reverse",
      paddingY: `${spacing3x3} !important`,
      paddingLeft: `${spacing12x} !important`,
      paddingRight: `${spacing4x3} !important`,
      width: "auto",
      minWidth: "auto",
      minHeight: "auto",
      lineHeight: spacing5x2,
      color: "font.primary",
      "> div": {
        padding: "unset",
        whiteSpace: "pre",
        flexWrap: "nowrap",
        "> div": {
          marginLeft: "0",
          marginRight: "0"
        },
        "[class$=singleValue]": {
          color: "inherit"
        }
      },
      _invalid: {
        borderColor: state.selectProps.colorScheme === "warning" ? "warning.600" : "danger.700",
        _hover: {
          borderColor: { md: state.selectProps.colorScheme === "warning" ? "warning.700" : "danger.800" }
        },
        _focus: {
          borderColor: "primary.500"
        }
      },
      input: {
        maxHeight: spacing6x3
      }
    })
  };
};

// src/components/Avatar.ts
var baseStyleContainer = {
  bg: "primary.700",
  color: "white"
};
var baseStyle3 = {
  container: baseStyleContainer
};
var getSize = (size) => ({
  container: {
    width: size,
    height: size,
    fontSize: `calc(${size} / 2)`
  },
  excessLabel: {
    width: size,
    height: size
  },
  label: {
    fontSize: `calc(${size} / 2)`,
    lineHeight: size !== "100%" ? size : void 0
  }
});
var sizes = {
  xs: getSize("24px"),
  sm: getSize("28px"),
  md: getSize("32px"),
  lg: getSize("40px"),
  xl: getSize("48px")
};
var defaultProps2 = {
  size: "xl"
};
var Avatar = {
  baseStyle: baseStyle3,
  sizes,
  defaultProps: defaultProps2
};
var Avatar_default = Avatar;

// src/components/Badge.ts
import { spacing4x as spacing4x4 } from "@airhelp/design-tokens";
var baseStyle4 = {
  fontSize: "sm",
  fontWeight: "bold",
  textTransform: "none",
  padding: spacing4x4,
  whiteSpace: "normal",
  width: {
    base: "100%",
    md: "fit-content"
  },
  div: {
    color: "primary.500"
  }
};
var variantBasic = {
  background: {
    base: "primary.200",
    md: "transparent"
  }
};
var variants2 = {
  basic: variantBasic
};
var defaultProps3 = {
  variant: "basic"
};
var Badge = {
  baseStyle: baseStyle4,
  variants: variants2,
  defaultProps: defaultProps3
};
var Badge_default = Badge;

// src/components/Banner.ts
var baseStyle5 = {
  container: {
    bg: "primary.500"
  },
  icon: {
    color: "greyscale.100"
  },
  title: {
    color: "greyscale.100"
  },
  subtitle: {
    color: "primary.200"
  },
  link: {
    color: "primary.200",
    textDecoration: "underline"
  },
  closeButton: {
    color: "primary.200"
  }
};
var variants3 = {
  secondary: {
    container: {
      bg: "greyscale.400"
    },
    icon: {
      color: "primary.500"
    },
    title: {
      color: "primary.900"
    },
    subtitle: {
      color: "greyscale.700"
    },
    link: {
      color: "primary.500",
      textDecoration: "none"
    },
    closeButton: {
      color: "greyscale.600"
    }
  },
  warning: {
    container: {
      bg: "warning.200"
    },
    icon: {
      color: "warning.500"
    },
    title: {
      color: "primary.900"
    },
    subtitle: {
      color: "greyscale.700"
    },
    link: {
      color: "primary.500",
      textDecoration: "none"
    },
    closeButton: {
      color: "greyscale.600"
    }
  },
  danger: {
    container: {
      bg: "danger.200"
    },
    icon: {
      color: "danger.500"
    },
    title: {
      color: "primary.900"
    },
    subtitle: {
      color: "greyscale.700"
    },
    link: {
      color: "primary.500",
      textDecoration: "none"
    },
    closeButton: {
      color: "greyscale.600"
    }
  },
  success: {
    container: {
      bg: "success.200"
    },
    icon: {
      color: "success.500"
    },
    title: {
      color: "primary.900"
    },
    subtitle: {
      color: "greyscale.700"
    },
    link: {
      color: "primary.500",
      textDecoration: "none"
    },
    closeButton: {
      color: "greyscale.600"
    }
  }
};
var Banner = { variants: variants3, baseStyle: baseStyle5 };
var Banner_default = Banner;

// src/components/Breadcrumb.ts
var baseStyleLink = {
  color: "primary.700",
  fontSize: "sm",
  _visited: {
    color: "primary.500",
    _hover: {
      color: "primary.600"
    }
  },
  _activeLink: { pointerEvents: "none" },
  _hover: {
    textDecoration: "none"
  },
  _active: {
    color: "primary.700"
  },
  _focus: {
    boxShadow: "none"
  }
};
var baseStyle6 = {
  link: baseStyleLink
};
var Breadcrumbs = {
  baseStyle: baseStyle6
};
var Breadcrumb_default = Breadcrumbs;

// src/components/Button.ts
var baseStyle7 = {
  borderRadius: "md",
  fontWeight: "medium",
  lineHeight: "base",
  whiteSpace: "normal",
  _loading: {
    opacity: 1
  },
  _disabled: {
    opacity: 1
  }
};
var variantPrimary = {
  color: "greyscale.100",
  background: "primary.500",
  _hover: {
    background: { md: "primary.600" },
    _disabled: {
      color: "primary.300",
      background: "primary.100"
    }
  },
  _active: {
    background: "primary.700"
  },
  _loading: {
    color: "greyscale.100",
    background: "primary.700",
    _hover: {
      color: "greyscale.100",
      background: "primary.700"
    }
  },
  _disabled: {
    color: "primary.300",
    background: "primary.100"
  }
};
var variantSecondary = {
  color: "primary.500",
  background: "greyscale.100",
  borderColor: "primary.500",
  border: "2px",
  _hover: {
    color: { md: "primary.600" },
    background: { md: "primary.100" },
    _disabled: {
      color: "primary.300"
    }
  },
  _active: {
    color: "primary.700",
    background: "greyscale.100"
  },
  _loading: {
    color: "primary.700",
    _hover: {
      color: "primary.700"
    }
  },
  _disabled: {
    color: "primary.300"
  }
};
var variantPrimaryAlternative = {
  color: "greyscale.100",
  background: "secondary.500",
  _hover: {
    color: { md: "secondary.500" },
    background: { md: "secondary.100" },
    _disabled: {
      color: "secondary.300",
      background: "secondary.100"
    }
  },
  _active: {
    color: "secondary.500",
    background: "secondary.200"
  },
  _loading: {
    color: "secondary.500",
    background: "secondary.200",
    _hover: {
      color: "secondary.500",
      background: "secondary.200"
    }
  },
  _disabled: {
    color: "secondary.300",
    background: "secondary.100"
  }
};
var variantSubtle = {
  color: "primary.500",
  background: "greyscale.100",
  _hover: {
    background: { md: "greyscale.200" }
  },
  _disabled: {
    color: "primary.300",
    _hover: {
      background: "greyscale.100"
    }
  },
  _active: {
    background: "greyscale.300"
  },
  _loading: {
    color: "primary.500",
    background: "greyscale.300",
    _hover: {
      color: "primary.500",
      background: "greyscale.300"
    }
  }
};
var variantDanger = {
  color: "greyscale.100",
  background: "danger.700",
  _hover: {
    background: { md: "danger.800" },
    _disabled: {
      background: "secondary.300"
    }
  },
  _active: {
    background: "danger.900"
  },
  _loading: {
    color: "greyscale.100",
    background: "danger.900",
    _hover: {
      color: "greyscale.100",
      background: "danger.900"
    }
  },
  _disabled: {
    background: "secondary.300"
  }
};
var variantLink = {
  p: 0,
  height: "auto",
  color: "primary.500",
  _hover: {
    textDecoration: "none",
    color: { md: "primary.600" }
  },
  _active: { color: "primary.700" },
  _visited: { color: "greyscale.500" }
};
var variants4 = {
  primary: variantPrimary,
  secondary: variantSecondary,
  primaryAlt: variantPrimaryAlternative,
  subtle: variantSubtle,
  danger: variantDanger,
  link: variantLink
};
var sizes2 = {
  xs: {
    fontSize: "sm",
    height: "auto",
    minH: 8,
    minW: 10,
    py: 2,
    px: 3,
    div: {
      boxSize: 4
    },
    svg: {
      boxSize: 4
    }
  },
  s: {
    fontSize: "sm",
    height: "auto",
    minH: 10,
    minW: 14,
    px: "18px",
    py: "10px",
    div: {
      boxSize: 5
    },
    svg: {
      boxSize: 5
    }
  },
  m: {
    fontSize: "md",
    px: 5,
    py: 3,
    height: "auto",
    minH: 12,
    minW: 16,
    div: {
      boxSize: 6
    },
    svg: {
      boxSize: 6
    }
  },
  l: {
    fontSize: "lg",
    height: "auto",
    minH: "60px",
    minW: "76px",
    px: 6,
    py: 4,
    div: {
      boxSize: 7
    },
    svg: {
      boxSize: 7
    }
  }
};
var defaultProps4 = {
  size: "m",
  variant: "primary"
};
var Button = {
  baseStyle: baseStyle7,
  variants: variants4,
  sizes: sizes2,
  defaultProps: defaultProps4
};
var Button_default = Button;

// src/components/Checkbox.ts
import { borderRadiusSm as borderRadiusSm3 } from "@airhelp/design-tokens";
var baseStyle8 = {
  control: {
    bg: "greyscale.100",
    border: "1px",
    borderColor: "greyscale.500",
    borderRadius: borderRadiusSm3,
    _focus: {
      boxShadow: "none"
    },
    _checked: {
      bg: "primary.500",
      borderColor: "primary.500",
      _hover: {
        bg: { md: "primary.600" },
        borderColor: { md: "primary.600" }
      },
      _disabled: {
        bg: "greyscale.400",
        borderColor: "greyscale.400",
        color: "greyscale.500"
      },
      _active: {
        bg: "primary.700",
        borderColor: "primary.700"
      }
    },
    _disabled: {
      bg: "greyscale.200",
      borderColor: "greyscale.400",
      pointerEvents: "none"
    },
    _hover: {
      borderColor: { md: "primary.300" },
      _invalid: {
        borderColor: { md: "danger.800" }
      }
    },
    _active: {
      borderColor: "primary.700"
    },
    _invalid: {
      borderColor: "danger.700",
      _checked: {
        borderColor: "primary.500",
        _hover: {
          borderColor: { md: "primary.500" }
        }
      }
    }
  },
  label: {
    color: "greyscale.700",
    a: {
      textDecoration: "underline"
    },
    _disabled: {
      color: "greyscale.600"
    }
  }
};
var sizes3 = {
  lg: {
    label: { fontSize: "sm" }
  }
};
var defaultProps5 = {
  size: "lg"
};
var Checkbox = {
  baseStyle: baseStyle8,
  sizes: sizes3,
  defaultProps: defaultProps5
};
var Checkbox_default = Checkbox;

// src/components/Collapsible.ts
var baseStyle9 = {
  container: {
    color: "font.primary"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    p: 4,
    cursor: "pointer",
    userSelect: "none"
  },
  title: {
    color: "font.primary",
    fontWeight: "medium"
  },
  headerInner: {
    display: "flex",
    color: "font.primary"
  },
  iconContainer: {
    mr: 2
  },
  actionButton: {
    svg: {
      color: "primary.500",
      boxSize: 6
    }
  },
  body: {
    position: "relative",
    mt: -2,
    fontSize: "md"
  }
};
var variants5 = {
  primary: {
    container: {
      bg: "greyscale.100"
    }
  },
  secondary: {
    container: {
      bg: "greyscale.300"
    }
  }
};
var Collapsible = { variants: variants5, baseStyle: baseStyle9 };
var Collapsible_default = Collapsible;

// src/components/Form.ts
var baseStyleHelperText = {
  fontSize: "sm",
  lineHeight: "base",
  fontWeight: "normal",
  color: "greyscale.600"
};
var baseStyle10 = {
  helperText: baseStyleHelperText
};
var Form = {
  baseStyle: baseStyle10
};
var Form_default = Form;

// src/components/FormError.ts
var baseStyleText = (props) => {
  const { colorScheme } = props;
  return {
    fontSize: "sm",
    fontWeight: "normal",
    lineHeight: "base",
    color: colorScheme === "warning" ? `${colorScheme}.600` : "danger.700"
  };
};
var baseStyle11 = (props) => ({
  text: baseStyleText(props)
});
var defaultProps6 = {
  colorScheme: "danger"
};
var FormError = {
  baseStyle: baseStyle11,
  defaultProps: defaultProps6
};
var FormError_default = FormError;

// src/components/FormLabel.ts
var baseStyle12 = {
  color: "font.primary"
};
var variantNormal = {
  fontSize: "sm",
  fontWeight: "medium",
  lineHeight: "base"
};
var variantSmall = {
  fontSize: "xs",
  fontWeight: "medium",
  lineHeight: "base"
};
var variantMuted = {
  fontSize: "sm",
  fontWeight: "normal",
  lineHeight: "base"
};
var variantCapitals = {
  fontSize: "xs",
  fontWeight: "medium",
  lineHeight: "base",
  textTransform: "uppercase"
};
var variantSmallMuted = {
  fontSize: "xs",
  fontWeight: "normal",
  lineHeight: "base"
};
var variantSmallCapitals = {
  fontSize: "10px",
  fontWeight: "medium",
  lineHeight: "base",
  textTransform: "uppercase"
};
var variants6 = {
  normal: variantNormal,
  small: variantSmall,
  capitals: variantCapitals,
  muted: variantMuted,
  smallMuted: variantSmallMuted,
  smallCapitals: variantSmallCapitals
};
var defaultProps7 = {
  variant: "normal"
};
var FormLabel = {
  baseStyle: baseStyle12,
  variants: variants6,
  defaultProps: defaultProps7
};
var FormLabel_default = FormLabel;

// src/components/Heading.ts
var baseStyle13 = {
  color: "font.primary"
};
var variants7 = {
  h1: {
    as: "h1",
    fontSize: ["2.25rem", null, "3rem"],
    lineHeight: "short"
  },
  h2: {
    as: "h2",
    fontSize: ["1.875rem", null, "2.25rem"],
    lineHeight: "short"
  },
  h3: {
    as: "h3",
    fontSize: ["1.5rem", null, "1.875rem"],
    lineHeight: "short"
  },
  h4: {
    as: "h4",
    fontSize: ["1.375rem", null, "1.5rem"],
    lineHeight: "base"
  },
  h5: {
    as: "h5",
    fontSize: "1.25rem",
    lineHeight: "base"
  }
};
var defaultProps8 = {
  variant: "h1"
};
var Heading = {
  baseStyle: baseStyle13,
  variants: variants7,
  defaultProps: defaultProps8
};
var Heading_default = Heading;

// src/components/InfoBox.ts
var baseStyle14 = {
  container: {
    display: "flex",
    alignItems: "flex-start",
    p: 4,
    borderRadius: "lg",
    color: "greyscale.700"
  },
  icon: { mr: 2 }
};
var variants8 = {
  primary: {
    container: {
      background: "greyscale.300"
    },
    icon: { color: "primary.500" }
  },
  secondary: {
    container: {
      border: "1px solid",
      borderColor: "greyscale.400",
      bg: "greyscale.100"
    },
    icon: { color: "greyscale.400" }
  },
  danger: {
    container: {
      border: "1px solid",
      borderColor: "danger.300",
      background: "danger.100"
    },
    icon: { color: "danger.500" }
  }
};
var InfoBox = { baseStyle: baseStyle14, variants: variants8 };
var InfoBox_default = InfoBox;

// src/components/Input.ts
import { spacing3x as spacing3x4, spacing4x as spacing4x5, spacing12x as spacing12x2 } from "@airhelp/design-tokens";
var variantOutline = (props) => {
  const { colorScheme } = props;
  return {
    field: {
      fontSize: "md",
      border: "1px",
      borderColor: "greyscale.500",
      borderRadius: "md",
      color: "font.primary",
      bg: "greyscale.100",
      paddingY: spacing3x4,
      paddingX: spacing4x5,
      height: spacing12x2,
      lineHeight: "base",
      _hover: {
        borderColor: { md: "primary.300" },
        _disabled: {
          opacity: 1,
          bg: "greyscale.300",
          borderColor: "greyscale.500"
        }
      },
      _focus: {
        boxShadow: "none",
        bg: "greyscale.100",
        borderColor: "primary.500"
      },
      _readOnly: {
        boxShadow: "none",
        userSelect: "all"
      },
      _disabled: {
        opacity: 1,
        color: "greyscale.500",
        bg: "greyscale.300"
      },
      _invalid: {
        boxShadow: "none",
        borderColor: colorScheme === "warning" ? `${colorScheme}.600` : "danger.700",
        _hover: {
          borderColor: { md: colorScheme === "warning" ? `${colorScheme}.700` : "danger.800" }
        },
        _focus: {
          borderColor: "primary.500"
        }
      },
      _placeholder: {
        color: "greyscale.500"
      }
    },
    addon: {
      background: "greyscale.300",
      borderColor: "greyscale.500",
      color: "greyscale.500",
      height: spacing12x2,
      minWidth: "65px"
    }
  };
};
var variants9 = {
  outline: variantOutline
};
var defaultProps9 = {
  colorScheme: "danger",
  variant: "outline"
};
var Input = {
  variants: variants9,
  defaultProps: defaultProps9
};
var Input_default = Input;

// src/components/Link.ts
var baseStyle15 = {
  fontWeight: "medium",
  lineHeight: "base",
  textDecoration: "none",
  _visited: {
    color: "greyscale.600",
    svgr: {
      color: "greyscale.600"
    }
  },
  _hover: {
    textDecoration: "none"
  }
};
var variantBasic2 = {
  color: "primary.500",
  _hover: {
    color: { md: "primary.600" }
  },
  _active: {
    color: "primary.700"
  }
};
var variantFooterLight = {
  color: "greyscale.600",
  _hover: {
    color: { md: "primary.500" }
  },
  _active: {
    color: "primary.700"
  }
};
var variantFooterDark = {
  color: "primary.900",
  _hover: {
    color: { md: "primary.500" }
  },
  _active: {
    color: "primary.700"
  },
  _visited: {
    color: "primary.900",
    svgr: {
      color: "primary.900"
    }
  }
};
var variants10 = {
  basic: variantBasic2,
  footerLight: variantFooterLight,
  footerDark: variantFooterDark
};
var sizes4 = {
  s: {
    fontSize: "sm",
    svg: {
      boxSize: 4
    }
  },
  m: {
    fontSize: "md",
    svg: {
      boxSize: 5
    }
  },
  l: {
    fontSize: "lg",
    svg: {
      boxSize: 6
    }
  }
};
var defaultProps10 = {
  variant: "basic",
  size: "m"
};
var Link = {
  baseStyle: baseStyle15,
  sizes: sizes4,
  variants: variants10,
  defaultProps: defaultProps10
};
var Link_default = Link;

// src/components/Modal.ts
import { borderRadiusMd as borderRadiusMd3, shadowsLg, spacing2x as spacing2x3, spacing3x as spacing3x5, spacing4x as spacing4x6, spacing6x as spacing6x4, spacing8x } from "@airhelp/design-tokens";
var baseStyleOverlay = {
  zIndex: "modal",
  background: "greyscale.400",
  opacity: "0.8!important"
};
var baseStyleDialogContainer = (props) => {
  const { isCentered, scrollBehavior } = props;
  return {
    display: "flex",
    zIndex: "modal",
    justifyContent: "center",
    alignItems: isCentered === false ? "flex-start" : "center",
    overflow: scrollBehavior === "inside" ? "scroll" : void 0
  };
};
var baseStyleDialog = {
  color: "primary.900",
  backgroundColor: "grayscale.100",
  borderRadius: borderRadiusMd3,
  boxShadow: shadowsLg,
  margin: "16px"
};
var baseStyleHeader = {
  fontSize: "20px",
  fontWeight: "bold",
  color: "font.primary",
  lineHeight: "base",
  paddingTop: 0,
  paddingBottom: { base: spacing6x4, md: spacing8x },
  paddingLeft: {
    base: spacing4x6,
    md: spacing8x
  },
  paddingRight: {
    base: "80px",
    md: "96px"
  },
  marginTop: { base: spacing4x6, md: spacing8x }
};
var baseStyleCloseButton = {
  top: { base: spacing4x6, md: spacing8x },
  right: { base: spacing4x6, md: spacing8x },
  padding: spacing3x5,
  width: "unset",
  height: "unset",
  color: "primary.900",
  background: "greyscale.300",
  cursor: "pointer",
  _focus: {
    outline: "none"
  },
  svg: {
    width: spacing4x6,
    height: spacing4x6
  }
};
var baseStyleBody = {
  fontWeight: "normal",
  fontSize: "md",
  paddingTop: "none",
  paddingBottom: spacing6x4,
  paddingX: {
    base: spacing4x6,
    md: spacing8x
  },
  overflow: "scroll",
  scrollBehavior: "smooth",
  "::-webkit-scrollbar": {
    width: spacing2x3
  },
  "::-webkit-scrollbar-track": {
    background: "greyscale.100",
    borderRadius: borderRadiusMd3
  },
  "::-webkit-scrollbar-thumb": {
    background: "greyscale.400",
    borderRadius: "3xl"
  },
  "::-webkit-scrollbar-thumb:hover": {
    background: { md: "greyscale.400" }
  }
};
var baseStyleFooter = {
  paddingTop: "unset",
  paddingX: {
    base: spacing4x6,
    md: spacing8x
  },
  paddingBottom: {
    base: spacing4x6,
    md: spacing8x
  },
  display: "flex",
  flexDirection: { base: "column", md: "row" },
  gap: spacing2x3,
  alignItems: "stretch",
  button: {
    width: {
      base: "100%",
      md: "unset"
    }
  }
};
var baseStyle16 = (props) => ({
  overlay: baseStyleOverlay,
  dialogContainer: baseStyleDialogContainer(props),
  dialog: baseStyleDialog,
  header: baseStyleHeader,
  closeButton: baseStyleCloseButton,
  body: baseStyleBody,
  footer: baseStyleFooter
});
var Modal = {
  baseStyle: baseStyle16
};
var Modal_default = Modal;

// src/components/Popover.ts
import { cssVar } from "@chakra-ui/theme-tools";
import { borderRadiusMd as borderRadiusMd4, spacing1x, spacing4x as spacing4x7 } from "@airhelp/design-tokens";
var $arrowShadowColor = cssVar("popper-arrow-shadow-color");
var baseStylePopper = {
  zIndex: 10
};
var baseStyleContent = {
  [$arrowShadowColor.variable]: "none",
  backgroundColor: "grayscale.100",
  color: "greyscale.700",
  boxShadow: "md",
  borderColor: "transparent",
  padding: spacing4x7,
  borderRadius: borderRadiusMd4,
  fontSize: "sm",
  fontWeight: "normal",
  lineHeight: "lineHeightBase",
  _focus: {
    outline: "none",
    boxShadow: "md"
  }
};
var baseStyleHeader2 = {
  fontWeight: "medium",
  marginBottom: spacing1x,
  padding: "unset",
  border: "none"
};
var baseStyleBody2 = {
  padding: "unset"
};
var baseStyle17 = {
  popper: baseStylePopper,
  content: baseStyleContent,
  header: baseStyleHeader2,
  body: baseStyleBody2,
  arrow: {}
};
var Popover = {
  baseStyle: baseStyle17
};
var Popover_default = Popover;

// src/components/Progress.ts
var baseStyleTrack = {
  bg: "greyscale.400",
  borderRadius: "8px"
};
var baseStyleFilledTrack = {
  bg: "primary.500"
};
var baseStyle18 = {
  filledTrack: baseStyleFilledTrack,
  track: baseStyleTrack
};
var sizes5 = {
  sm: {
    track: {
      w: 9,
      h: 1
    }
  }
};
var defaultProps11 = {
  size: "sm"
};
var Progress = {
  sizes: sizes5,
  baseStyle: baseStyle18,
  defaultProps: defaultProps11
};
var Progress_default = Progress;

// src/components/Radio.ts
import { spacing2x as spacing2x4, spacing4x as spacing4x8, borderRadiusMd as borderRadiusMd5 } from "@airhelp/design-tokens";
var disabledStyle = {
  borderColor: "greyscale.400",
  background: "greyscale.100",
  span: {
    borderColor: "greyscale.400",
    background: "greyscale.200",
    _checked: {
      borderColor: "greyscale.400",
      background: "greyscale.100",
      _before: {
        background: "greyscale.400"
      }
    }
  }
};
var baseStyle19 = {
  container: {
    padding: spacing4x8,
    border: "1px solid",
    borderColor: "greyscale.500",
    borderRadius: borderRadiusMd5,
    background: "greyscale.100",
    marginTop: "-1px !important",
    _disabled: disabledStyle,
    _hover: {
      zIndex: 1,
      borderColor: { md: "primary.300" },
      _disabled: {
        ...disabledStyle,
        "span:first-of-type": {
          borderColor: "greyscale.400",
          background: "greyscale.200",
          _before: {
            background: "greyscale.200"
          }
        }
      },
      "span:first-of-type": {
        borderColor: { md: "primary.300" },
        _before: {
          md: {
            background: "primary.300",
            content: "''",
            display: "inline-block",
            position: "relative",
            w: spacing2x4,
            h: spacing2x4,
            borderRadius: "50%"
          }
        }
      }
    },
    _active: {
      borderColor: "greyscale.500",
      background: "greyscale.300",
      _disabled: {
        ...disabledStyle
      },
      span: {
        borderColor: "primary.700",
        _before: {
          background: "primary.700"
        },
        _checked: {
          background: "greyscale.300"
        }
      },
      _hover: {
        borderColor: "greyscale.500",
        background: "greyscale.300",
        _disabled: {
          ...disabledStyle
        },
        span: {
          borderColor: "primary.700",
          _before: {
            background: "greyscale.100"
          }
        },
        _checked: {
          "span:first-of-type": {
            background: "greyscale.100",
            _before: {
              background: "primary.700"
            }
          }
        }
      }
    },
    _checked: {
      zIndex: 1,
      borderColor: "primary.500",
      _disabled: disabledStyle,
      _hover: {
        borderColor: { md: "primary.300" },
        _disabled: disabledStyle
      },
      _active: {
        borderColor: "greyscale.500",
        _disabled: disabledStyle
      }
    }
  },
  control: {
    ...Checkbox_default.baseStyle.control,
    borderRadius: "full",
    _checked: {
      ...Checkbox_default.baseStyle.control._checked,
      background: "greyscale.100",
      _hover: {
        borderColor: { md: "primary.300" },
        background: { md: "greyscale.100" },
        _before: {
          background: { md: "primary.300" }
        }
      },
      _active: {
        borderColor: "primary.700",
        _before: {
          background: "primary.700"
        }
      },
      _before: {
        w: spacing2x4,
        h: spacing2x4,
        bg: "primary.500"
      }
    }
  },
  label: {
    ...Checkbox_default.baseStyle.label,
    width: "100%"
  }
};
var variants11 = {
  top: {
    container: {
      borderBottomRadius: 0
    }
  },
  bottom: {
    container: {
      borderTopRadius: 0
    }
  },
  left: {
    container: {
      borderRightRadius: 0
    }
  },
  right: {
    container: {
      borderLeftRadius: 0
    }
  },
  center: {
    container: {
      borderRadius: 0
    }
  },
  rounded: {
    container: {
      borderRadius: borderRadiusMd5
    }
  }
};
var defaultProps12 = {
  variant: "rounded"
};
var Radio = {
  baseStyle: baseStyle19,
  defaultProps: defaultProps12,
  variants: variants11
};
var Radio_default = Radio;

// src/components/Spinner.ts
import { cssVar as cssVar2 } from "@chakra-ui/theme-tools";
import { spacing1x as spacing1x2, spacing4x as spacing4x9, spacing5x as spacing5x3, spacing6x as spacing6x5, spacing8x as spacing8x2, spacing12x as spacing12x3, spacing10x } from "@airhelp/design-tokens";
var $size = cssVar2("spinner-size");
var baseStyle20 = {
  width: [$size.reference],
  height: [$size.reference],
  color: "primary.500",
  margin: "unset !important"
};
var variantContentMedium = {
  [$size.variable]: spacing12x3,
  borderWidth: "6px"
};
var variantContentLarge = {
  [$size.variable]: "4rem",
  borderWidth: "7px"
};
var sizes6 = {
  xs: {
    [$size.variable]: spacing4x9
  },
  sm: {
    [$size.variable]: spacing5x3
  },
  md: {
    [$size.variable]: spacing6x5,
    borderWidth: "3px"
  },
  lg: {
    [$size.variable]: spacing8x2,
    borderWidth: "3px"
  },
  xl: {
    [$size.variable]: spacing10x,
    borderWidth: spacing1x2
  }
};
var defaultProps13 = {
  size: "md"
};
var variants12 = {
  contentLarge: variantContentLarge,
  contentMedium: variantContentMedium
};
var Spinner = {
  baseStyle: baseStyle20,
  sizes: sizes6,
  variants: variants12,
  defaultProps: defaultProps13
};
var Spinner_default = Spinner;

// src/components/Switch.ts
import { cssVar as cssVar3 } from "@chakra-ui/theme-tools";
import { shadowsBase, spacing6x as spacing6x6, spacing10x as spacing10x2 } from "@airhelp/design-tokens";
var $width = cssVar3("switch-track-width");
var $height = cssVar3("switch-track-height");
var baseStyleTrack2 = {
  background: "greyscale.300",
  borderRadius: "18px",
  padding: "unset",
  transitionProperty: "common",
  transitionDuration: "fast",
  _hover: {
    background: { md: "primary.300" },
    _checked: {
      background: { md: "primary.600" }
    }
  },
  _active: {
    background: "greyscale.500",
    _checked: {
      background: "primary.700"
    }
  },
  _focus: {
    boxShadow: "none"
  },
  _disabled: {
    background: "greyscale.400",
    _hover: {
      background: "greyscale.400"
    },
    _active: {
      background: "greyscale.400"
    },
    _checked: {
      background: "greyscale.300",
      _hover: {
        background: "greyscale.300"
      },
      _active: {
        background: "greyscale.300"
      }
    }
  },
  _checked: {
    background: "primary.500"
  }
};
var baseStyleThumb = {
  background: "greyscale.100",
  boxShadow: shadowsBase,
  width: "22px",
  height: "22px",
  position: "absolute",
  top: "1px",
  left: "1px"
};
var sizes7 = {
  md: {
    container: {
      [$width.variable]: spacing10x2,
      [$height.variable]: spacing6x6
    }
  }
};
var defaultProps14 = {
  size: "md"
};
var baseStyle21 = {
  track: baseStyleTrack2,
  thumb: baseStyleThumb
};
var Switch = {
  baseStyle: baseStyle21,
  sizes: sizes7,
  defaultProps: defaultProps14
};
var Switch_default = Switch;

// src/components/Text.ts
var baseStyle22 = {
  color: "font.primary"
};
var variantLarge = {
  fontSize: "lg",
  lineHeight: "base",
  fontWeight: "normal"
};
var variantMedium = {
  fontSize: "md",
  lineHeight: "base",
  fontWeight: "normal"
};
var variants13 = {
  large: variantLarge,
  medium: variantMedium
};
var defaultProps15 = {
  variant: "large"
};
var Text = {
  baseStyle: baseStyle22,
  variants: variants13,
  defaultProps: defaultProps15
};
var Text_default = Text;

// src/components/Textarea.ts
var variants14 = {
  outline: (props) => {
    var _a;
    return (_a = Input_default.variants.outline(props).field) != null ? _a : {};
  }
};
var defaultProps16 = {
  variant: "outline"
};
var Textarea = {
  variants: variants14,
  defaultProps: defaultProps16
};
var Textarea_default = Textarea;

// src/components/Tabs.ts
import { spacing1x as spacing1x3, spacing4x as spacing4x10, spacing6x as spacing6x7 } from "@airhelp/design-tokens";
var baseStyle23 = {
  root: {
    display: "flex",
    overflowX: "auto",
    msOverflowStyle: "none",
    scrollbarWidth: "none",
    "::-webkit-scrollbar": {
      display: "none"
    },
    paddingX: { base: `${spacing4x10}`, lg: 0 },
    width: { base: "100vw", lg: "100%" },
    marginLeft: { base: `-${spacing4x10}`, lg: 0 }
  },
  tablist: {
    color: "greyscale.600",
    flexBasis: "100%"
  },
  tab: {
    padding: `0 ${spacing6x7} ${spacing1x3} !important`,
    margin: "2px 0px",
    whiteSpace: "nowrap",
    _selected: {
      color: "primary.900",
      borderBottomColor: "primary.500 !important",
      fontWeight: "medium",
      backgroundColor: "transparent"
    },
    _hover: { color: "greyscale.600", borderBottomColor: "primary.300" }
  }
};
var Tabs = { baseStyle: baseStyle23 };
var Tabs_default = Tabs;

// src/components/Tag.ts
var baseStyle24 = {
  container: { borderRadius: "24px", textTransform: "uppercase" }
};
var sizes8 = {
  s: {
    container: {
      minW: "59px",
      minH: "28px",
      fontSize: "xs",
      fontWeight: 500,
      lineHeight: "20px",
      letterSpacing: "1.5px",
      px: 3,
      py: 1
    }
  },
  xs: {
    container: {
      minW: "44px",
      minH: "23px",
      fontSize: "10px",
      fontWeight: 700,
      lineHeight: "15px",
      letterSpacing: "0.5px",
      px: 2,
      py: 1
    }
  }
};
var variants15 = {
  primary: ({ colorScheme }) => ({
    container: {
      color: "greyscale.100",
      bg: colorScheme === "info" ? `${colorScheme.replace("info", "primary")}.400` : `${colorScheme}.500`
    }
  }),
  secondary: ({ colorScheme }) => ({
    container: {
      bg: colorScheme === "info" ? `${colorScheme.replace("info", "primary")}.100` : `${colorScheme}.200`,
      color: colorScheme === "info" ? `${colorScheme.replace("info", "primary")}.400` : `${colorScheme}.500`
    }
  }),
  tertiary: ({ colorScheme }) => ({
    container: {
      border: "1px solid",
      borderColor: colorScheme === "info" ? `${colorScheme.replace("info", "primary")}.400` : `${colorScheme}.500`,
      color: colorScheme === "info" ? `${colorScheme.replace("info", "primary")}.400` : `${colorScheme}.500`,
      bg: `${colorScheme.replace("info", "primary")}.100`
    }
  }),
  quaternary: ({ colorScheme }) => ({
    container: {
      bg: "none",
      color: colorScheme === "info" ? `${colorScheme.replace("info", "primary")}.400` : `${colorScheme}.500`
    }
  })
};
var defaultProps17 = {
  size: "s",
  colorScheme: "info",
  variant: "primary"
};
var Tag = {
  variants: variants15,
  baseStyle: baseStyle24,
  sizes: sizes8,
  defaultProps: defaultProps17
};
var Tag_default = Tag;

// src/components/index.ts
var components = {
  Alert: Alert_default,
  Accordion: Accordion_default,
  Avatar: Avatar_default,
  Badge: Badge_default,
  Banner: Banner_default,
  Breadcrumb: Breadcrumb_default,
  Button: Button_default,
  Checkbox: Checkbox_default,
  Form: Form_default,
  FormError: FormError_default,
  FormLabel: FormLabel_default,
  Heading: Heading_default,
  Radio: Radio_default,
  Input: Input_default,
  Link: Link_default,
  Modal: Modal_default,
  Popover: Popover_default,
  Progress: Progress_default,
  InfoBox: InfoBox_default,
  Spinner: Spinner_default,
  Switch: Switch_default,
  Text: Text_default,
  Textarea: Textarea_default,
  Collapsible: Collapsible_default,
  Tag: Tag_default,
  Tabs: Tabs_default,
  getAutocompleteStyles,
  getAutocompleteWithIconStyles,
  getSelectStyles
};
var components_default = components;

// src/colors.ts
import {
  colorPrimary100,
  colorPrimary200,
  colorPrimary300,
  colorPrimary400,
  colorPrimary500,
  colorPrimary600,
  colorPrimary700,
  colorPrimary800,
  colorPrimary900,
  colorSecondary100,
  colorSecondary200,
  colorSecondary300,
  colorSecondary400,
  colorSecondary500,
  colorSecondary600,
  colorSecondary700,
  colorSecondary800,
  colorSecondary900,
  colorGreyscale100,
  colorGreyscale200,
  colorGreyscale300,
  colorGreyscale400,
  colorGreyscale500,
  colorGreyscale600,
  colorGreyscale700,
  colorGreyscale800,
  colorGreyscale900,
  colorSuccess100,
  colorSuccess200,
  colorSuccess300,
  colorSuccess400,
  colorSuccess500,
  colorSuccess600,
  colorSuccess700,
  colorSuccess800,
  colorSuccess900,
  colorWarning100,
  colorWarning200,
  colorWarning300,
  colorWarning400,
  colorWarning500,
  colorWarning600,
  colorWarning700,
  colorWarning800,
  colorWarning900,
  colorDanger100,
  colorDanger200,
  colorDanger300,
  colorDanger400,
  colorDanger500,
  colorDanger600,
  colorDanger700,
  colorDanger800,
  colorDanger900
} from "@airhelp/design-tokens";
var colors = {
  font: {
    primary: colorPrimary900
  },
  primary: {
    100: colorPrimary100,
    200: colorPrimary200,
    300: colorPrimary300,
    400: colorPrimary400,
    500: colorPrimary500,
    600: colorPrimary600,
    700: colorPrimary700,
    800: colorPrimary800,
    900: colorPrimary900
  },
  secondary: {
    100: colorSecondary100,
    200: colorSecondary200,
    300: colorSecondary300,
    400: colorSecondary400,
    500: colorSecondary500,
    600: colorSecondary600,
    700: colorSecondary700,
    800: colorSecondary800,
    900: colorSecondary900
  },
  greyscale: {
    100: colorGreyscale100,
    200: colorGreyscale200,
    300: colorGreyscale300,
    400: colorGreyscale400,
    500: colorGreyscale500,
    600: colorGreyscale600,
    700: colorGreyscale700,
    800: colorGreyscale800,
    900: colorGreyscale900
  },
  success: {
    100: colorSuccess100,
    200: colorSuccess200,
    300: colorSuccess300,
    400: colorSuccess400,
    500: colorSuccess500,
    600: colorSuccess600,
    700: colorSuccess700,
    800: colorSuccess800,
    900: colorSuccess900
  },
  warning: {
    100: colorWarning100,
    200: colorWarning200,
    300: colorWarning300,
    400: colorWarning400,
    500: colorWarning500,
    600: colorWarning600,
    700: colorWarning700,
    800: colorWarning800,
    900: colorWarning900
  },
  danger: {
    100: colorDanger100,
    200: colorDanger200,
    300: colorDanger300,
    400: colorDanger400,
    500: colorDanger500,
    600: colorDanger600,
    700: colorDanger700,
    800: colorDanger800,
    900: colorDanger900
  }
};
var colors_default = colors;

// src/sizes.ts
var sizes9 = {
  "18": "4.5rem"
};
var sizes_default = sizes9;

// src/styles.ts
var styles = {
  global: {
    body: {
      color: "primary.900"
    }
  }
};
var styles_default = styles;

// src/typography.ts
import {
  lineHeightNone,
  lineHeightShort,
  lineHeightBase,
  fontFamilyMain as fontFamilyMain2,
  fontSizeXs,
  fontSizeSm as fontSizeSm2,
  fontSizeMd,
  fontSizeLg,
  fontSizeXl,
  fontSize2xl,
  fontSize3xl,
  fontSize4xl,
  fontSize5xl
} from "@airhelp/design-tokens";
var typography = {
  lineHeights: {
    none: lineHeightNone,
    short: lineHeightShort,
    shorter: lineHeightShort,
    base: lineHeightBase
  },
  fonts: {
    body: fontFamilyMain2,
    heading: fontFamilyMain2
  },
  fontSizes: {
    xs: fontSizeXs,
    sm: fontSizeSm2,
    md: fontSizeMd,
    lg: fontSizeLg,
    xl: fontSizeXl,
    "2xl": fontSize2xl,
    "3xl": fontSize3xl,
    "4xl": fontSize4xl,
    "5xl": fontSize5xl
  }
};
var typography_default = typography;

// src/shadows.ts
import {
  shadowsNone,
  shadowsBase as shadowsBase2,
  shadowsMd,
  shadowsLg as shadowsLg2,
  shadowsAlt
} from "@airhelp/design-tokens";
var shadows = {
  none: shadowsNone,
  base: shadowsBase2,
  md: shadowsMd,
  lg: shadowsLg2,
  alt: shadowsAlt
};
var shadows_default = shadows;

// src/radiuses.ts
import {
  borderRadiusSm as borderRadiusSm4,
  borderRadiusMd as borderRadiusMd6,
  borderRadiusLg,
  borderRadiusXl,
  borderRadius2xl,
  borderRadius3xl
} from "@airhelp/design-tokens";
var radiuses = {
  sm: borderRadiusSm4,
  md: borderRadiusMd6,
  lg: borderRadiusLg,
  xl: borderRadiusXl,
  "2xl": borderRadius2xl,
  "3xl": borderRadius3xl
};
var radiuses_default = radiuses;

// src/index.ts
var theme = extendTheme2({
  colors: colors_default,
  components: components_default,
  sizes: sizes_default,
  styles: styles_default,
  shadows: shadows_default,
  radii: radiuses_default,
  ...typography_default
});
export {
  localizeTheme,
  theme
};
